import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Grid, Button, Skeleton, Tabs, Tab, Paper, Fade 
} from '@mui/material';
import { AgendamentoService } from '../../services/agendamentoservice';
import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { InfoAgendamento } from '../agendamento/InfoAgendamento';
import { AgendamentoStep } from '../../agendamento.types';

export const StepHorario: React.FC<{ servicoId: number; unidadeId: number }> = ({ servicoId, unidadeId }) => {
  const { setHorario, setProfissionaisDisponiveis, setStep, horario: horarioSelecionado, data: dataSelecionadaStore } = useAgendamentoStore();
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [mapaGeral, setMapaGeral] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const carregarAgendas = async () => {
      try {
        const dados = await AgendamentoService.listarHorarios(servicoId, unidadeId);
        setProfissionaisDisponiveis(dados);
        
        const consolidado: Record<string, Set<string>> = {};
        
        dados.forEach((prof) => {
          Object.entries(prof.diaHorarios).forEach(([data, horas]) => {
            if (!consolidado[data]) consolidado[data] = new Set();
            (horas as string[]).forEach(h => consolidado[data].add(h.substring(0, 5)));
          });
        });

        const mapaFinal: Record<string, string[]> = {};
        Object.keys(consolidado).forEach(data => {
          mapaFinal[data] = Array.from(consolidado[data]).sort();
        });

        setMapaGeral(mapaFinal);
      } catch (error) {
        console.error("Erro ao carregar horários", error);
      } finally {
        setLoading(false);
      }
    };

    carregarAgendas();
  }, [servicoId, unidadeId, setProfissionaisDisponiveis]);

  const handleSelectHorario = (data: string, hora: string) => {
    setHorario(data, hora);
    setStep(AgendamentoStep.PROFISSIONAIS as number);
  };

  if (loading) return <Skeleton variant="rounded" height={300} sx={{ borderRadius: 6, bgcolor: 'rgba(255,255,255,0.05)' }} />;

  const datasDisponiveis = Object.keys(mapaGeral).sort();
  const dataAtiva = datasDisponiveis[tabIndex];
  const horariosDoDia = mapaGeral[dataAtiva] || [];

  return (
    <Fade in timeout={500}>
      <Box>
        <InfoAgendamento />
        
        <Typography variant="h6" fontWeight={800} mb={2} sx={{ color: 'var(--color-text)' }}>Selecione o Dia</Typography>
        <Tabs 
          value={tabIndex} 
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            mb: 4, 
            '& .MuiTabs-indicator': { bgcolor: 'var(--color-primary)' },
            '& .MuiTab-root': { color: 'var(--color-text)', opacity: 0.7 },
            '& .MuiTab-root.Mui-selected': { color: 'var(--color-primary)', fontWeight: 800 }
          }}
        >
          {datasDisponiveis.map((data) => (
            <Tab 
              key={data} 
              label={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {new Date(data + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short' })}
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {new Date(data + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                  </Typography>
                </Box>
              } 
            />
          ))}
        </Tabs>

        <Typography variant="h6" fontWeight={800} mb={2} sx={{ color: 'var(--color-text)' }}>Horários disponíveis</Typography>
        <Grid container spacing={2}>
          {horariosDoDia.map((hora) => {
            const isSelected = dataAtiva === dataSelecionadaStore && hora === horarioSelecionado;
            
            return (
              <Grid key={hora} size={{ xs: 4, sm: 3, md: 2 }}>
                <Button
                  fullWidth
                  variant={isSelected ? "contained" : "outlined"}
                  onClick={() => handleSelectHorario(dataAtiva, hora)}
                  sx={{ 
                    borderRadius: 3, 
                    py: 1.5,
                    fontWeight: 700,
                    bgcolor: isSelected ? 'var(--color-primary)' : 'transparent',
                    color: isSelected ? 'var(--color-background)' : 'var(--color-text)',
                    borderColor: isSelected ? 'var(--color-primary)' : 'rgba(128,128,128,0.2)',
                    '&:hover': { 
                      borderColor: 'var(--color-primary)', 
                      bgcolor: isSelected ? 'var(--color-primary)' : 'rgba(255,255,255,0.05)' 
                    }
                  }}
                >
                  {hora}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Fade>
  );
};